import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import OrangeFixProofCard from "@/shared/OrangeFixProofCard";

// Phase 3E-B — Customer-facing Orange Fix status page.
// Public, no-auth. Reads only what `resolve_public_token` allows.
// Shows: issue, quote, work status, verification. Nothing else.

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const CUR_SYM = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
const fmtMoney = (m, cur) => `${CUR_SYM[cur] || (cur + " ")}${(Number(m || 0)/100).toFixed(2)}`;

const STEPS = [
  { key: "quote",      label: "Quote",           statuses: ["QUOTED", "AWAITING_PAYMENT", "PAID", "IN_PROGRESS", "WORK_COMPLETED", "VERIFYING", "VERIFIED_RESOLVED", "STILL_DETECTED"] },
  { key: "payment",    label: "Payment",         statuses: ["PAID", "IN_PROGRESS", "WORK_COMPLETED", "VERIFYING", "VERIFIED_RESOLVED", "STILL_DETECTED"] },
  { key: "work",       label: "Work",            statuses: ["WORK_COMPLETED", "VERIFYING", "VERIFIED_RESOLVED", "STILL_DETECTED"] },
  { key: "verified",   label: "Verified by Optimise", statuses: ["VERIFIED_RESOLVED"] },
];

export default function CustomerFixStatus() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND}/api/fix/${token}`)
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("This link isn't valid or has been withdrawn."));
    return () => { alive = false; };
  }, [token]);

  if (err) return (
    <div className="min-h-screen bg-[#FFFDF6] flex items-center justify-center px-6"
         data-testid="fix-public-error">
      <div className="max-w-md text-center">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#FF6A00] font-black">Orange Fix</div>
        <h1 className="mt-2 font-black text-3xl tracking-[-0.02em]">Link not available</h1>
        <p className="mt-3 text-[#4A4A4A]">{err}</p>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-[#FFFDF6] flex items-center justify-center"
         data-testid="fix-public-loading">
      <div className="text-[#8A8A8A] tracking-[0.24em] uppercase text-sm">Loading…</div>
    </div>
  );

  const verified = data.status === "VERIFIED_RESOLVED";
  const still    = data.status === "STILL_DETECTED";

  return (
    <div className="min-h-screen bg-[#FFFDF6] px-6 py-16" data-testid="fix-public-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#FF6A00] font-black">Orange Fix</div>
        <h1 className="mt-2 font-black text-3xl md:text-4xl tracking-[-0.02em]"
            data-testid="fix-public-issue">
          {data.issue_title}
        </h1>
        <p className="mt-1 text-[13px] tracking-[0.14em] uppercase text-[#8A8A8A] font-bold">
          {data.category || "issue"}
        </p>

        <div className="mt-8 space-y-4">
          {data.quote_amount != null && (
            <Row label="Quote" value={fmtMoney(data.quote_amount, data.currency)}
                 testId="fix-public-quote"/>
          )}
          <Row label="Status" value={friendly(data.status)} testId="fix-public-status" strong/>
        </div>

        {/* Payment CTA if awaiting payment */}
        {data.public_payment_url && ["AWAITING_PAYMENT", "QUOTED"].includes(data.status) && (
          <a href={data.public_payment_url}
             data-testid="fix-public-pay-btn"
             className="mt-8 inline-flex items-center px-6 py-3 rounded-full bg-[#FF6A00] text-black text-[11px] font-black tracking-[0.22em] uppercase hover:bg-[#e75f00]">
            Pay now
          </a>
        )}

        {/* Verified banner */}
        {verified && !data.proof && (
          <div className="mt-8 p-5 rounded-2xl border-2 border-[#0FA958] bg-[#EBFBF2]"
               data-testid="fix-public-verified">
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#0FA958]">
              Fix verified ✓
            </div>
            <p className="mt-2 text-[15px] text-[#0A0A0A]">
              Orange Optimise rescanned your website and confirmed that this
              issue is no longer detected.
            </p>
            {data.verified_at && (
              <p className="mt-2 text-[12px] text-[#4A4A4A]">
                Verified {new Date(data.verified_at).toLocaleDateString()}
              </p>
            )}
          </div>
        )}

        {/* Verified — Phase 3F proof card */}
        {verified && data.proof && (
          <div className="mt-8" data-testid="fix-public-proof-wrapper">
            <div className="mb-3 flex justify-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#EBFBF2] border-2 border-[#0FA958] text-[#0FA958] font-black text-[11px] tracking-[0.22em] uppercase"
                    data-testid="fix-public-issue-resolved">
                Issue resolved ✓
              </span>
            </div>
            <OrangeFixProofCard proof={data.proof} customer/>
          </div>
        )}

        {/* Still detected banner */}
        {still && (
          <div className="mt-8 p-5 rounded-2xl border-2 border-[#C1350B] bg-[#FFF7EE]"
               data-testid="fix-public-still-detected">
            <div className="text-[10px] tracking-[0.28em] uppercase font-black text-[#C1350B]">
              Issue still detected
            </div>
            <p className="mt-2 text-[15px] text-[#0A0A0A]">
              Our team is continuing the remediation without any additional
              charge. We'll re-verify once the follow-up work is complete.
            </p>
          </div>
        )}

        {/* Milestone chips */}
        <div className="mt-10 flex flex-wrap gap-2" data-testid="fix-public-steps">
          {STEPS.map((step) => {
            const done = step.statuses.includes(data.status);
            return (
              <span key={step.key}
                    data-testid={`fix-public-step-${step.key}`}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase ${
                      done ? "bg-[#0A0A0A] text-white" : "bg-white border border-[#EAEAEA] text-[#8A8A8A]"
                    }`}>
                {done ? "✓ " : ""}{step.label}
              </span>
            );
          })}
        </div>

        <p className="mt-14 text-[11px] tracking-[0.22em] uppercase text-[#8A8A8A]">
          OrangeBySite · Orange Fix
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, testId, strong }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-3 border-b border-[#EAEAEA] items-center">
      <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A]">{label}</div>
      <div className={`col-span-2 ${strong ? "font-black text-lg" : "text-[15px]"}`}
           data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

function friendly(status) {
  return ({
    REQUESTED: "Fix requested",
    REVIEWING: "Under review",
    QUOTED: "Quote ready",
    AWAITING_PAYMENT: "Awaiting payment",
    PAID: "Payment received — ready for work",
    IN_PROGRESS: "Work in progress",
    WORK_COMPLETED: "Work completed — verifying",
    VERIFYING: "Verifying with Orange Optimise",
    VERIFIED_RESOLVED: "Fix verified ✓",
    STILL_DETECTED: "Issue still detected",
    CANCELLED: "Cancelled",
  })[status] || status;
}
